import React, {
    Component
} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {enumerate} from 'ts-transformer-enumerate';
import {v4 as uuidV4} from 'uuid';
import _ from 'lodash';
import memoize from 'memoize-one';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import TreeNode from 'primereact/treenode/TreeNode';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import User from '~/shared/src/model/User';
import {Query} from '~/shared/src/utilities/query';
import {
    ColumnDefinition,
    ColumnConfig,
    ColumnSorter,
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {ClearConfigParameters} from '~/shared/src/utilities/modules/form';
import {
    LoadDataParameters,
    LoadColumnDataDescriptorsParameters,
    LoadColumnDataDescriptorsResult,
    GetOverviewItemIdByDisplayAttributeResult
} from '~/shared/src/utilities/modules/overview';
import {
    extractDataObjectsFromDataStructure,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';

import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Headerbar from '~/frontend/src/components/base/OverviewDesigner/Headerbar';
import Toolbar from '~/frontend/src/components/base/OverviewDesigner/Toolbar';
import OverviewDesigner, {
    DragData,
    DragOptions,
    Mode as OverviewDesignerMode,
    ReferenceType as OverviewDesignerReferenceType
} from '~/frontend/src/components/base/OverviewDesigner';
import TreeTable, {
    TreeTable as TreeTableClass,
    DefaultProps as TreeTableDefaultProps
} from '~/frontend/src/components/base/OverviewDesigner/TreeTable';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Form from '~/frontend/src/components/base/Form';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import ValueSetField from '~/frontend/src/components/base/shared/field/ValueSetField';
import TextField from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';

import {
    isDataModified,
    isDataModifiedRecursive,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {overviewUtilities} from '~/frontend/src/components/base/OverviewDesigner/utilities';
import {
    getPanelElusiveData,
    getPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import * as CiActions from '~/frontend/src/actions/data';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import loadData from '~/frontend/src/actions/data/loadData';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean
};


const schemata           = enumerate<YnSchema>(),
      staticQueryTypes   = enumerate<Exclude<Query['type'], 'DataStructure'>>(),
      generatedKeyRegExp = new RegExp( `^(${staticQueryTypes.join( '|' )}):(.{8,8})$`, 'g' );


type Variables = {
    [ variable : string ] : any;
};

const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext: 'overviewDesigner' as string,
    //
    title: 'OverviewDesignerView' as string,
    //
    schema: 'DATA' as Schema,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width: 20 as number,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status,

    /**
     * The timestamp to filter data at.
     */
    timestamp: 'now' as string,

    /**
     * The timezone to filter data at.
     */
    timezone: 'Europe/Berlin' as string,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    mode: 'design' as OverviewDesignerMode,

    standalone: true as boolean,

    showData: false as boolean,

    showHiddenColumns: false as boolean
};

type DefaultProps = Readonly<typeof defaultProps>;

/**
 * Definition of components props
 * @memberof TreeTest
 * @property {String} dataContext='cypher' The ci-data context of this component
 * @property {Object} items CI-Data as stored in redux store
 * @property {String} itemsVersion Version of CI-Data as stored in redux store
 */
type Props = {
    uid : string;

    /**
     * The ItemId to filter data and metadata for.
     */
    itemId? : number;

    // the ciId of the existing form
    rootItemId : number

    // the key of the new or existing form
    rootItemKey : string;

    // the name of the new or existing form
    rootItemName : string;

    // the id of the root item to display data in the form
    dataRootItemId? : number;

    payloadItemType? : string;

    isQueryOverview? : boolean;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode? : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    selectedLayoutElementId? : number;

    referenceToCreateForNew? : ReferenceAttributesForCreation;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( rootItemId : number, dataContext : string ) => void;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: string
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: string
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    //updatePanelProps: typeof updatePanelProps;
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    deleteReference : DeleteReference;
    addItem : AddItem;
    addReference : AddReference;

};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Props & DefaultProps & ActionProps;
export type ComponentProps = Partial<DefaultProps> & Props;


type State = {
    // the ciId of the currently selected tree node
    rootItemId : number;
    selectedItemParentId? : number;

    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];
    rootItemFormState : 'CLEAR' | 'DIRTY' | 'ERROR';

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    initialized : boolean;

    overviewItemTypeId : number;

    showCreateNewOverviewDialog : boolean;
    createNewOverviewDialogQueryOnly : boolean;

    createNewOverviewDialogKey? : string;
    createNewOverviewDialogKeyError? : string;
    createNewOverviewDialogType? : Query['type'];
    createNewOverviewDialogSchema : Schema;
    createNewOverviewDialogItemType? : string;
    createNewOverviewDialogString? : string;
    createNewOverviewDialogStringError? : string;
    dynamicColumns? : string[];

    mobile : boolean;
};


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


// parameter globalDataVersions NEEDED for memoize!!!
const getQueryParams = memoize(
    ( rootItemId : number, dataContext : string, globalDataVersions : PanelElusiveData[ 'data' ] ) => {
        return overviewUtilities.getQuery( dataContext, rootItemId )?.parameters;
    }
);


/**
 * Playground to test Tree implementation
 */
class OverviewDesignerView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'OverviewDesignerView';
    static panelTabTitle = 'YN.base.frontend.overviewDesigner.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Overview';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.overviewDesigner';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType      : Joi.string().valid( OverviewDesignerView.panelType ),
            panelTabTitle  : Joi.string().default( OverviewDesignerView.panelTabTitle ),
            panelTabIcon   : Joi.string().default( OverviewDesignerView.panelTabIcon ),
            rootItemId     : Joi.number().description( 'Id of existing overview.' ),
            rootItemKey    : Joi.string().description( 'Key of overview' ),
            rootItemName   : Joi.string().description( 'Name of overview' ),
            payloadItemType: Joi.string().description( 'Item Type of the payload root item (only needed for new overviews)' ),
            isQueryOverview: Joi.boolean().description( 'Is the overview a query (SQL or Cypher) based one.' ),
            menuWidth      : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menu2Width     : Joi.number()
                .default( 30 )
                .description( 'The width of the right menu panel in percent of the available width.' ),
            // this is only for compatibility with ItemFinder overview (if we open this panel from there)
            rawMode                : rawModeSchema,
            showAdditionalRawNames : showAdditionalRawNamesSchema,
            referenceToCreateForNew: Joi.object( {
                srcItemId     : Joi.number()
                    .required(),
                destItemId    : Joi.number()
                    .required(),
                referenceSetId: Joi.number()
                    .required(),
                statusId      : Joi.number()
                    .required()
            } ),
            onSave                 : Joi.function()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( props.dataContext ) {
                delete global.data[ props.dataContext ];
                delete global.data[ `${props.dataContext}-meta` ];
                delete global.data[ `${props.dataContext}_data` ];
                delete global.data[ `${props.dataContext}_DataObjects` ];
            }

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await OverviewDesignerView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await OverviewDesignerView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'rootItemKey'> ) : string | undefined {
        const {
                  dataContext,
                  rootItemId,
                  rootItemKey
              }      = props,
              itemId = rootItemId
                  ?? dataUtils.findItem( dataContext, 'Overview', rootItemKey )?.id
                  ?? 0,
              item   = global.data[ dataContext ]?.items?.[ itemId ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${OverviewDesignerView.panelType}/${encodeURIComponent( item.displayAttributeValue )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = rootItem.displayAttributeValue
        if ( params?.[ 1 ] ) {
            const displayAttributeValue = decodeURIComponent( params[ 1 ] );

            // resolve viewKey and rootItemId
            const res    = await xhr.post(
                      `${BASE_URL}/api/overview/getOverviewItemIdByDisplayAttribute`,
                      {
                          displayAttributeValue
                      }
                  ),
                  result = res?.data as GetOverviewItemIdByDisplayAttributeResult;

            if ( result ) {
                if ( result.data.rootItemId ) {
                    retVal.rootItemId = result.data.rootItemId;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              elusiveData                                        = getPanelElusiveData( uid ),
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  rootItemId,
                  selectedLayoutElementId,
                  onSave
              }                                                  = propsWithDefaults,
              rootItemIdForSave                                  = rootItemId ?? elusiveData?.data?.rootItemId,
              result                                             = await dispatch( saveData( uid, 'CFG', rootItemIdForSave, undefined, undefined, undefined, successMsgParams ) );

        if ( selectedLayoutElementId && result && result.data.itemMetaDataMapping[ String( selectedLayoutElementId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedLayoutElementId', result.data.itemMetaDataMapping[ String( selectedLayoutElementId ) as unknown as number ].id ) );
        }

        if ( onSave ) {
            if ( result && result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id, uid );
            }
            else {
                onSave( rootItemIdForSave, uid );
            }
        }

        return Promise.resolve( result );
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = OverviewDesignerView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }

    menuContentPanelContentStyle = { padding: 0 };
    menuContentPanelsPt = {
        gutter: {
            style: {
                display: 'none'
            }
        }
    };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    tree : TreeTableClass | null = null;
    overviewDesigner : OverviewDesignerReferenceType | null = null;


    getTreeContextMenu = ( treeData : TreeNode.TreeNode[], key : string ) => [
        {
            label  : 'Collapse all',
            icon   : 'far fa-minus-square',
            command: function() {
                this.setState( { [ key ]: {} } );
            }.bind( this )
        },
        {
            label  : 'Expand all',
            icon   : 'far fa-plus-square',
            command: function() {
                const expandedKeys : {
                    [ key : string ] : true
                } = {};
                const expandNode = ( node : TreeNode.TreeNode ) => {
                    if ( node.children?.length ) {
                        expandedKeys[ node.key! ] = true;

                        for ( const child of node.children ) {
                            expandNode( child );
                        }
                    }
                }

                for ( const node of treeData ) {
                    expandNode( node );
                }

                this.setState( { [ key ]: expandedKeys } );
            }.bind( this )
        }
    ];

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            rootItemId: 0 as number,

            isDirty          : false,
            errorCount       : 0,
            rootItemFormState: 'CLEAR',

            scrollIndicator: 0 as number,

            initialized: false,

            overviewItemTypeId: metaModel[ 'CFG' ].getItemType( 'Overview' )?.id ?? 0,

            showCreateNewOverviewDialog     : false,
            createNewOverviewDialogQueryOnly: !!props.isQueryOverview,

            createNewOverviewDialogKey     : props.rootItemKey ? props.rootItemKey : undefined,
            createNewOverviewDialogSchema  : 'DATA',
            createNewOverviewDialogType    : 'DataStructure',
            createNewOverviewDialogItemType: props.payloadItemType ? props.payloadItemType : undefined,

            mobile: getMobile()
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getOverviewDesigner = memoize( this.getOverviewDesigner, this.getOverviewDesignerEqual );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getSelectableSchemata = memoize( this.getSelectableSchemata );
        this.getToolbar = memoize( this.getToolbar, this.getToolbarEqual );
        this.getTreeTable = memoize( this.getTreeTable, this.getTreeTableEqual );
        this.setPanelInfo = memoize( this.setPanelInfo );

        this.checkKeyUniqueness = _.throttle( this.checkKeyUniqueness, 300, { trailing: true } );
        this.checkQuery = _.throttle( this.checkQuery, 500, { trailing: true } );
        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.checkForCreateNewOverviewPrerequisits = this.checkForCreateNewOverviewPrerequisits.bind( this );
        this.onCreateNewOverviewDialogHide = this.onCreateNewOverviewDialogHide.bind( this );
        this.onCreateNewOverviewDialogOkButtonClick = this.onCreateNewOverviewDialogOkButtonClick.bind( this );
        this.onCreateNewOverviewDialogSetItemType = this.onCreateNewOverviewDialogSetItemType.bind( this );
        this.onCreateNewOverviewDialogSetKey = this.onCreateNewOverviewDialogSetKey.bind( this );
        this.onCreateNewOverviewDialogSetSchema = this.onCreateNewOverviewDialogSetSchema.bind( this );
        this.onCreateNewOverviewDialogSetString = this.onCreateNewOverviewDialogSetString.bind( this );
        this.onCreateNewOverviewDialogSetType = this.onCreateNewOverviewDialogSetType.bind( this );
        this.onMarkFieldsForAlias = this.onMarkFieldsForAlias.bind( this );
        this.onMarkLayoutElementsForAlias = this.onMarkLayoutElementsForAlias.bind( this );
        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );
        this.onNewLayoutElementDragStart = this.onNewLayoutElementDragStart.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSelectLayoutElement = this.onSelectLayoutElement.bind( this );
        this.onShowDataChange = this.onShowDataChange.bind( this );
        this.onShowHiddenColumnsChange = this.onShowHiddenColumnsChange.bind( this );
        this.onSortersChange = this.onSortersChange.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  rootItemId,
                  rootItemKey,
                  dataContext,
                  subPanelProps,
                  allElusiveData
              }                                              = props,
              tooltipErrors : ( string | React.ReactNode )[] = [],
              messageErrors : ( string | React.ReactNode )[] = [];

        let isDirty : boolean                              = rootItemId ? isDataModifiedRecursive( dataContext, 'CFG', rootItemId ) : isDataModified( dataContext, 'CFG' ),
            errorCount                                     = 0,
            rootItemFormState : State['rootItemFormState'] = 'CLEAR';

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps   = subPanelProps?.[ panelKey ],
                              formRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName  = formRootItem
                                  ? formRootItem.name
                                      ? formRootItem.name
                                      : formRootItem?.key
                                  : '',
                              elementType  = formRootItem ? formRootItem.itemType.label : 'Element';

                        isDirty = isDirty || !!panelElusiveData.isDirty;

                        if ( formRootItem?.id === rootItemId ) {
                            if ( panelElusiveData.errorData?.count ) {
                                rootItemFormState = 'ERROR';
                            }
                            else if ( panelElusiveData.isDirty ) {
                                rootItemFormState = 'DIRTY';
                            }
                        }

                        if ( panelElusiveData.errorData?.count ) {
                            errorCount += panelElusiveData.errorData.count;

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        <div className="yn-tooltip-header2">
                                            {__( 'YN.base.frontend.overviewDesigner.error.tooltip.title', { type: elementType, name: elementName } )}
                                        </div>
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );

                                if ( panelElusiveData.errorData.message?.length ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( 'YN.base.frontend.overviewDesigner.error.message.title', { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        return {
            rootItemId  : rootItemId || ( dataContext && rootItemKey ? dataUtils.findItem( dataContext, 'Overview', rootItemKey )?.id ?? 0 : state.rootItemId ),
            isDirty,
            errorCount,
            errorTooltip: errorCount
                ? <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>
                : undefined,
            errorMessage: errorCount
                ? <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>
                : undefined,
            rootItemFormState
        };
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        if ( !this.props.elusiveData ) {
            if ( this.props.rootItemId || this.props.rootItemKey ) {
                void this.loadData();
            }
            else {
                this.checkForCreateNewOverviewPrerequisits();
            }
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        this.afterRender();

        // key changed or dialog shown
        if (
            prevState.createNewOverviewDialogKey !== this.state.createNewOverviewDialogKey
            || ( !prevState.showCreateNewOverviewDialog && this.state.showCreateNewOverviewDialog )
        ) {
            this.setState( { createNewOverviewDialogKeyError: __( 'YN.base.finder.item.key.check.unique' ) } );
            this.checkKeyUniqueness();
        }

        // type or string changed
        if (
            prevState.createNewOverviewDialogType !== this.state.createNewOverviewDialogType
            || prevState.createNewOverviewDialogString !== this.state.createNewOverviewDialogString
        ) {
            this.checkQuery();
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        if (
            ( nextState.isDirty !== undefined && nextState.isDirty !== this.state.isDirty )
        ) {
            this.props.setPanelDirty( this.props.uid, nextState.isDirty );
        }

        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  uid
              }      = this.props,
              retVal = await this.props.loadData?.(
                  'overview/loadConfigData',
                  dataContext,
                  dataContext,
                  undefined,
                  'CFG',
                  {
                      params   : rootItemId ? {
                          viewId: rootItemId
                      } : {
                          viewKey: rootItemKey
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  resonseData => {
                      if ( resonseData?.data.rootItemId && rootItemId !== resonseData.data.rootItemId ) {
                          this.props.setParam( uid, 'rootItemId', resonseData.data.rootItemId );
                      }

                      if ( !resonseData?.data.items.length ) {
                          this.checkForCreateNewOverviewPrerequisits();
                      }
                      else {
                          this.setState( {
                              initialized: true
                          } );
                      }
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  intercomMessages
              }                                             = props,
              {
                  isDirty,
                  errorCount
              }                                             = this.state,
              icon                                          = this.getPanelIcon( OverviewDesignerView.panelTabIcon ),
              itemTitleConfig : ItemTitleConfig | undefined = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              title                                         = this.getPanelTitle( OverviewDesignerView.panelTabTitle, undefined, itemTitleConfig ),
              path                                          = OverviewDesignerView.buildPanelPathFromProps( { dataContext, rootItemId, rootItemKey } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    checkForCreateNewOverviewPrerequisits() {
        const {
                  createNewOverviewDialogQueryOnly,
                  createNewOverviewDialogItemType
              } = this.state;

        // query based overview or no root DataObject ItemType -> show dialog
        if ( createNewOverviewDialogQueryOnly || !createNewOverviewDialogItemType ) {
            this.setState( {
                initialized                : true,
                showCreateNewOverviewDialog: true
            } );
        }
        else {
            this.setState( {
                initialized: true
            } );

            this.createNewOverview();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  uid
              }             = this.props, {
                  overviewItemTypeId,
                  createNewOverviewDialogKey
              }             = this.state,
              compopnentUid = `${uid}_check`,
              requestUid    = 'checkKeyUniqueness';

        if ( overviewItemTypeId && createNewOverviewDialogKey ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( compopnentUid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : 'CFG',
                          status  : 'all',
                          itemType: overviewItemTypeId,
                          key     : createNewOverviewDialogKey
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          compopnentUid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { createNewOverviewDialogKeyError: __( 'YN.base.frontend.overviewDesigner.createNewDialog.key.error.notUnique' ) } );
                    }
                    else {
                        this.setState( { createNewOverviewDialogKeyError: '' } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    checkQuery() {
        const {
                  dataContext,
                  uid
              }             = this.props, {
                  createNewOverviewDialogSchema,
                  createNewOverviewDialogType,
                  createNewOverviewDialogString
              }             = this.state,
              compopnentUid = `${uid}_check`,
              requestUid    = 'checkQuery';

        this.setState( { dynamicColumns: undefined } );

        if ( createNewOverviewDialogType && createNewOverviewDialogType !== 'DataStructure' && createNewOverviewDialogString ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( compopnentUid, requestUid );
            }

            const doLoad = async () => {
                const queryParameters : string[] | null            = createNewOverviewDialogString.match( /(?<=\$\{?)([\w\p{L}\p{N}]|\.)+(?=\}|[^\w\p{L}\p{N}]|$)/gmu ),
                      params : LoadColumnDataDescriptorsParameters = {
                          type      : createNewOverviewDialogType,
                          schema    : createNewOverviewDialogSchema,
                          query     : createNewOverviewDialogString,
                          parameters: queryParameters ? queryParameters : undefined
                      };

                await this.props.loadData?.(
                    'overview/loadColumnDataDescriptors',
                    dataContext,
                    dataContext,
                    'loadColumnDataDescriptors',
                    'CFG',
                    {
                        params
                    },
                    undefined,
                    ( result ) => {
                        const responseData = result as unknown as LoadColumnDataDescriptorsResult;

                        if ( !responseData.success ) {
                            this.setState( { createNewOverviewDialogStringError: __( 'YN.base.frontend.overviewDesigner.createNewDialog.string.error.notExecutable' ) } );
                        }
                        else {
                            let columns : string[] | undefined = [];

                            for ( const columnDataDescriptor of responseData.data.columnDataDescriptors as StaticDataDescriptor[] ) {
                                columns.push( columnDataDescriptor.field );
                            }

                            if ( !columns.length ) {
                                columns = undefined;
                                this.setState( { createNewOverviewDialogStringError: __( 'YN.base.frontend.overviewDesigner.createNewDialog.string.error.notExecutable' ) } );
                            }

                            this.setState( { dynamicColumns: columns } );
                        }
                    }
                );
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    createNewOverview() {
        const {
                  uid,
                  dataContext,
                  referenceToCreateForNew,
                  rootItemName
              }                         = this.props,
              {
                  overviewItemTypeId,
                  createNewOverviewDialogKey,
                  createNewOverviewDialogSchema,
                  createNewOverviewDialogItemType,
                  createNewOverviewDialogType,
                  createNewOverviewDialogString,
                  dynamicColumns
              }                         = this.state,
              activeStatusId            = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
              columnItemTypeId          = metaModel[ 'CFG' ].getItemType( 'Column' )!.id,
              columnOrderPropertyTypeId = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Column', 'Order' )!.id;

        if ( createNewOverviewDialogKey ) {
            if ( createNewOverviewDialogItemType ) {
                const overview       = this.props.addItem(
                          dataContext,
                          {
                              itemTypeId: overviewItemTypeId,
                              key       : createNewOverviewDialogKey,
                              name      : rootItemName ?? createNewOverviewDialogItemType,
                              statusId  : activeStatusId
                          },
                          'CFG'
                      ),
                      query          = this.props.addItem(
                          dataContext,
                          {
                              itemTypeId: metaModel[ 'CFG' ].getItemType( 'Query' )!.id,
                              key       : `${createNewOverviewDialogKey}:${uuidV4().substring( 0, 8 )}`,
                              name      : 'DataStructure',
                              statusId  : activeStatusId,
                              properties: [
                                  {
                                      propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Query', 'Type' )!.id,
                                      value        : 'DataStructure',
                                      statusId     : activeStatusId
                                  }
                              ]
                          },
                          'CFG'
                      ),
                      rootDataObject = this.props.addItem(
                          dataContext,
                          {
                              itemTypeId: metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                              key       : `${createNewOverviewDialogKey}:${createNewOverviewDialogItemType}:${uuidV4().substring( 0, 8 )}`,
                              name      : createNewOverviewDialogItemType,
                              statusId  : activeStatusId,
                              properties: [
                                  {
                                      propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectSchema' )!.id,
                                      value        : createNewOverviewDialogSchema.toLowerCase(),
                                      statusId     : activeStatusId
                                  },
                                  {
                                      propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectType' )!.id,
                                      value        : createNewOverviewDialogItemType,
                                      statusId     : activeStatusId
                                  }
                              ]
                          },
                          'CFG'
                      ),
                      column         = this.props.addItem(
                          dataContext,
                          {
                              itemTypeId: columnItemTypeId,
                              key       : `${createNewOverviewDialogKey}:${createNewOverviewDialogItemType}:name:${uuidV4().substring( 0, 8 )}`,
                              name      : `${createNewOverviewDialogItemType} name`,
                              statusId  : activeStatusId,
                              properties: [
                                  {
                                      propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Column', 'AttributePath' )!.id,
                                      value        : 'item.name',
                                      statusId     : activeStatusId
                                  },
                                  {
                                      propertySetId: columnOrderPropertyTypeId,
                                      value        : 10,
                                      statusId     : activeStatusId
                                  }
                              ]
                          }, 'CFG' );

                if ( overview && query && rootDataObject ) {
                    this.props.addReference(
                        dataContext,
                        {
                            srcItemId     : query.id,
                            destItemId    : overview.id,
                            referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Query', 'Overview', 'getsDataFor', 'src' )![ 0 ].id,
                            statusId      : activeStatusId
                        },
                        'CFG'
                    );
                    this.props.addReference(
                        dataContext,
                        {
                            srcItemId     : rootDataObject.id,
                            destItemId    : query.id,
                            referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Query', 'isRootOf', 'src' )![ 0 ].id,
                            statusId      : activeStatusId
                        },
                        'CFG'
                    );

                    if ( column ) {
                        this.props.addReference(
                            dataContext,
                            {
                                srcItemId     : column.id,
                                destItemId    : overview.id,
                                referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Column', 'Overview', 'belongsTo', 'src' )![ 0 ].id,
                                statusId      : activeStatusId
                            },
                            'CFG'
                        );
                        this.props.addReference(
                            dataContext,
                            {
                                srcItemId     : rootDataObject.id,
                                destItemId    : column.id,
                                referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Column', 'providesDataFor', 'src' )![ 0 ].id,
                                statusId      : activeStatusId
                            },
                            'CFG'
                        );
                    }

                    // add additionalReference (replace rootItemId by form.id)
                    if ( referenceToCreateForNew ) {
                        if ( referenceToCreateForNew.srcItemId < 0 ) {
                            referenceToCreateForNew.srcItemId = overview.id;
                        }
                        else {
                            referenceToCreateForNew.destItemId = overview.id;
                        }

                        this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                        this.props.setParam( uid, 'referenceToCreateForNew', undefined );
                    }

                    this.props.setParam( uid, 'rootItemId', overview.id );
                }
            }
            else if ( createNewOverviewDialogType === 'Cypher' || createNewOverviewDialogType === 'SQL' ) {
                const overview = this.props.addItem(
                          dataContext,
                          {
                              itemTypeId: overviewItemTypeId,
                              key       : createNewOverviewDialogKey,
                              name      : rootItemName ?? 'Overview',
                              statusId  : activeStatusId
                          },
                          'CFG'
                      ),
                      query    = this.props.addItem(
                          dataContext,
                          {
                              itemTypeId: metaModel[ 'CFG' ].getItemType( 'Query' )!.id,
                              key       : `${createNewOverviewDialogKey}:${uuidV4().substring( 0, 8 )}`,
                              name      : createNewOverviewDialogType,
                              statusId  : activeStatusId,
                              properties: [
                                  {
                                      propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Query', 'Type' )!.id,
                                      value        : createNewOverviewDialogType,
                                      statusId     : activeStatusId
                                  },
                                  ...(
                                      createNewOverviewDialogString
                                          ? [
                                              {
                                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Query', 'Query' )!.id,
                                                  value        : createNewOverviewDialogString,
                                                  statusId     : activeStatusId
                                              }
                                          ]
                                          : []
                                  )
                              ]
                          },
                          'CFG'
                      );

                if ( overview && query ) {
                    this.props.addReference(
                        dataContext,
                        {
                            srcItemId     : query.id,
                            destItemId    : overview.id,
                            referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Query', 'Overview', 'getsDataFor', 'src' )![ 0 ].id,
                            statusId      : activeStatusId
                        },
                        'CFG'
                    );

                    if ( dynamicColumns?.length ) {
                        const columnQueryFieldNamePropertyTypeId = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Column', 'QueryFieldName' )!.id;

                        for ( let i = 0; i < dynamicColumns.length; i++ ) {
                            const field  = dynamicColumns[ i ],
                                  column = this.props.addItem(
                                      dataContext,
                                      {
                                          itemTypeId: columnItemTypeId,
                                          key       : `${createNewOverviewDialogKey}:${field}:${uuidV4().substring( 0, 8 )}`,
                                          name      : field,
                                          statusId  : activeStatusId,
                                          properties: [
                                              {
                                                  propertySetId: columnQueryFieldNamePropertyTypeId,
                                                  value        : field,
                                                  statusId     : activeStatusId
                                              },
                                              {
                                                  propertySetId: columnOrderPropertyTypeId,
                                                  value        : 10 * ( i + 1 ),
                                                  statusId     : activeStatusId
                                              }
                                          ]
                                      }, 'CFG' );

                            if ( column ) {
                                this.props.addReference(
                                    dataContext,
                                    {
                                        srcItemId     : column.id,
                                        destItemId    : overview.id,
                                        referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Column', 'Overview', 'belongsTo', 'src' )![ 0 ].id,
                                        statusId      : activeStatusId
                                    },
                                    'CFG'
                                );
                            }
                        }
                    }

                    // add additionalReference (replace rootItemId by form.id)
                    if ( referenceToCreateForNew ) {
                        if ( referenceToCreateForNew.srcItemId < 0 ) {
                            referenceToCreateForNew.srcItemId = overview.id;
                        }
                        else {
                            referenceToCreateForNew.destItemId = overview.id;
                        }

                        this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                        this.props.setParam( uid, 'referenceToCreateForNew', undefined );
                    }

                    this.props.setParam( uid, 'rootItemId', overview.id );
                }
            }
        }
    }

    onReloadButtonClick() {
        const { uid, dataContext } = this.props;

        if ( dataContext ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean data
            global.data[ dataContext + '-meta' ] = {
                items     : [],
                references: [],
                schema    : global.data[ dataContext + '-meta' ].schema
            };
            global.data[ dataContext ] = {
                items     : [],
                references: [],
                schema    : global.data[ dataContext ].schema
            };

            this.props.setMessage?.( this.props.uid + '_treeview', 'notification', 'reload', true );
            this.props.setMessage?.( this.props.uid + '_DataObjects', 'notification', 'reload', true );

            if ( this.props.rootItemId || this.props.rootItemKey ) {

                window.setTimeout(
                    () => {
                        void this.loadData();
                    },
                    1
                );
            }
        }
    }

    onShowDataChange( showData : boolean ) {
        this.props.setParam( this.props.uid, 'showData', showData );
    }

    onSortersChange( newSorters : ColumnSorter[] ) : void {
        this.overviewDesigner?.setSorters( newSorters );
    }

    onShowHiddenColumnsChange( showHiddenColumns : boolean ) {
        this.props.setParam( this.props.uid, 'showHiddenColumns', showHiddenColumns );
    }

    onMenuScroll() {
        this.setState( { scrollIndicator: this.state.scrollIndicator! + 1 } )
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onSelectLayoutElement( selectedLayoutElementId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedLayoutElementId', selectedLayoutElementId );
    }

    onCreateNewOverviewDialogOkButtonClick() {
        this.createNewOverview();
        this.setState( { showCreateNewOverviewDialog: false } );
    }

    onCreateNewOverviewDialogHide() {
        this.props.closePanel( this.props.uid );
    }

    onCreateNewOverviewDialogSetKey( value : string | undefined ) {
        let keyError = '';

        if ( !value ) {
            keyError = __( 'YN.base.finder.item.key.error.empty' );
        }
        else if ( / /.test( value ) ) {
            keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
        }

        this.setState( { createNewOverviewDialogKey: value, createNewOverviewDialogKeyError: keyError } );
    }

    onCreateNewOverviewDialogSetType( value : AttributeValue | undefined ) {
        const type = value as Query['type'];

        const {
                  createNewOverviewDialogItemType,
                  createNewOverviewDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewOverviewDialogSchema === 'CFG'
                  ? `CFG:${createNewOverviewDialogItemType ?? ''}`
                  : createNewOverviewDialogItemType ?? '';

        let {
                createNewOverviewDialogKey,
                createNewOverviewDialogKeyError
            } = this.state;

        if ( this.state.createNewOverviewDialogType === 'DataStructure' && ( !createNewOverviewDialogKey || createNewOverviewDialogKey === oldGeneratedKey ) ) {
            createNewOverviewDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewOverviewDialogKeyError = undefined;
        }

        if ( !createNewOverviewDialogKey || createNewOverviewDialogKey === oldGeneratedKey || createNewOverviewDialogKey.match( generatedKeyRegExp ) ) {
            if ( type === 'DataStructure' ) {
                createNewOverviewDialogKey = oldGeneratedKey;
                createNewOverviewDialogKeyError = undefined;
            }
            else {
                createNewOverviewDialogKey = `${type}:${uuidV4().substring( 0, 8 )}`;
                createNewOverviewDialogKeyError = undefined;
            }
        }

        this.setState( {
            createNewOverviewDialogType       : type,
            createNewOverviewDialogStringError: type === 'DataStructure'
                ? undefined
                : this.state.createNewOverviewDialogString,
            createNewOverviewDialogKey,
            createNewOverviewDialogKeyError,
            dynamicColumns                    : undefined
        } );
    }

    onCreateNewOverviewDialogSetSchema( value : string ) {
        const {
                  createNewOverviewDialogItemType,
                  createNewOverviewDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewOverviewDialogSchema === 'CFG'
                  ? `CFG:${createNewOverviewDialogItemType ?? ''}`
                  : createNewOverviewDialogItemType ?? '';

        let {
                createNewOverviewDialogKey,
                createNewOverviewDialogKeyError
            } = this.state;

        if ( this.state.createNewOverviewDialogType === 'DataStructure' && ( !createNewOverviewDialogKey || createNewOverviewDialogKey === oldGeneratedKey ) ) {
            createNewOverviewDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewOverviewDialogKeyError = undefined;
        }

        this.setState( {
            createNewOverviewDialogSchema  : value as Schema,
            createNewOverviewDialogItemType: undefined,
            createNewOverviewDialogKey,
            createNewOverviewDialogKeyError
        } );
    }

    onCreateNewOverviewDialogSetItemType( value : string ) {
        const {
                  createNewOverviewDialogItemType,
                  createNewOverviewDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewOverviewDialogSchema === 'CFG'
                  ? `CFG:${createNewOverviewDialogItemType ?? ''}`
                  : createNewOverviewDialogItemType ?? '';

        let {
                createNewOverviewDialogKey,
                createNewOverviewDialogKeyError
            } = this.state;

        if ( this.state.createNewOverviewDialogType === 'DataStructure' && ( !createNewOverviewDialogKey || createNewOverviewDialogKey === oldGeneratedKey ) ) {
            createNewOverviewDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewOverviewDialogKeyError = undefined;
        }

        if ( this.state.createNewOverviewDialogType === 'DataStructure' && ( !createNewOverviewDialogKey || createNewOverviewDialogKey.match( generatedKeyRegExp ) ) ) {
            createNewOverviewDialogKey = createNewOverviewDialogSchema === 'CFG' ? 'CFG:' + value : value;
            createNewOverviewDialogKeyError = undefined;
        }

        this.setState( {
            createNewOverviewDialogItemType: value,
            createNewOverviewDialogKey,
            createNewOverviewDialogKeyError
        } );
    }

    onCreateNewOverviewDialogSetString( value : string | undefined ) {
        let stringError = '';

        if ( !value ) {
            stringError = __( 'YN.base.frontend.overviewDesigner.createNewDialog.string.error.empty' );
        }

        this.setState( { createNewOverviewDialogString: value, createNewOverviewDialogStringError: stringError } );
    }

    onNewLayoutElementDragStart( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) : void {
        this.overviewDesigner?.startDrag( rootLayoutElementId, data, options, e );
    }

    onMarkLayoutElementsForAlias( alias : ( string | undefined ) ) : void {
        this.overviewDesigner?.markLayoutElementsForAlias( alias );
    }

    onMarkFieldsForAlias( alias : ( string | string[] | undefined ) ) : void {
        this.tree?.markFieldsForAlias( alias );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    getSelectableSchemata( user? : User ) {
        const retVal : Schema[] = [];

        for ( const schema of schemata ) {
            const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

            for ( const itemType of itemTypes ) {
                if (
                    itemType.id > 0
                    && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                ) {
                    retVal.push( schema );
                    break;
                }
            }
        }

        retVal.sort();

        return retVal;
    }

    getCreateNewOverviewDialog() {
        const {
                  uid,
                  user
              }                           = this.props,
              {
                  showCreateNewOverviewDialog,
                  createNewOverviewDialogQueryOnly,
                  createNewOverviewDialogKey,
                  createNewOverviewDialogKeyError,
                  createNewOverviewDialogType,
                  createNewOverviewDialogSchema,
                  createNewOverviewDialogItemType,
                  createNewOverviewDialogString,
                  createNewOverviewDialogStringError,
                  dynamicColumns,
                  mobile
              }                           = this.state,
              selectableSchemata          = this.getSelectableSchemata( user ),
              style : React.CSSProperties = {
                  width    : !createNewOverviewDialogType || createNewOverviewDialogType === 'DataStructure'
                      ? '33rem'
                      : '60vw',
                  minWidth : !createNewOverviewDialogType || createNewOverviewDialogType === 'DataStructure'
                      ? '33rem'
                      : '60vw',
                  minHeight: '25vh'
              };

        // only one selectable schemata and not set -> set it
        if ( selectableSchemata.length === 1 && createNewOverviewDialogSchema !== selectableSchemata[ 0 ] ) {
            this.setState( { createNewOverviewDialogSchema: selectableSchemata[ 0 ] } );
            return;
        }

        let okDisabled = true;

        if (
            createNewOverviewDialogKey
            && !createNewOverviewDialogKeyError
            && createNewOverviewDialogType
            && (
                !!(
                    createNewOverviewDialogType === 'DataStructure'
                    && createNewOverviewDialogItemType
                )
                || !!(
                    createNewOverviewDialogType !== 'DataStructure'
                    && !createNewOverviewDialogStringError
                    && createNewOverviewDialogString
                    && dynamicColumns
                )
            )
        ) {
            okDisabled = false;
        }

        if ( !createNewOverviewDialogType || createNewOverviewDialogType === 'DataStructure' ) {
            style[ '--min-width' ] = '33rem';
        }
        else if ( !mobile ) {
            style[ '--min-width' ] = '60vw';
        }

        return <Dialog
            key={`${uid}_createNewOverviewDialog`}
            id={`${uid}_createNewOverviewDialog`}

            visible={showCreateNewOverviewDialog}
            onHide={this.onCreateNewOverviewDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-form-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel .p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.overviewDesigner.createNewDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={__( 'YN.base.common.ok' )}
                    className="p-button-outlined mr-3"
                    disabled={okDisabled}
                    onClick={this.onCreateNewOverviewDialogOkButtonClick}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onCreateNewOverviewDialogHide}
                />
            </div>}
        >
            <StringField
                uid={`${uid}_keyField`}
                label={__( 'YN.base.frontend.overviewDesigner.createNewDialog.key.label' )}
                value={createNewOverviewDialogKey}
                errorMessage={createNewOverviewDialogKeyError}
                style={{ width: createNewOverviewDialogQueryOnly ? '20rem' : '30rem' }}
                labelPosition="top"
                infoTextLayout="hidden"
                markDirty={false}
                onChange={this.onCreateNewOverviewDialogSetKey}
            />
            <ValueSetField
                uid={`${uid}_queryTypeField`}
                schema="CFG"
                label={__( 'YN.base.frontend.overviewDesigner.createNewDialog.queryType.label' )}
                value={createNewOverviewDialogType}
                propertySetId={metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Query', 'Type' )?.id ?? 0}
                blacklist={createNewOverviewDialogQueryOnly ? [ 'DataStructure' ] : undefined}
                style={{ width: createNewOverviewDialogQueryOnly ? '20rem' : '30rem' }}
                labelPosition="top"
                presentation="button"
                infoTextLayout="hidden"
                allowNone={false}
                markDirty={false}
                onChange={this.onCreateNewOverviewDialogSetType}
            />
            {createNewOverviewDialogType === 'DataStructure' &&
                <FieldWrapper
                    uid={`${uid}_queryItemTypeField`}
                    label={__( 'YN.base.frontend.overviewDesigner.createNewDialog.queryItemType.label' )}
                    labelPosition="top"
                    infoTextLayout="hidden"
                    isDirty={false}
                    autoComplete={false}
                >
                    {selectableSchemata.length > 1 &&
                        <SchemaDropdown
                            uid={uid}
                            value={createNewOverviewDialogSchema}
                            onChange={this.onCreateNewOverviewDialogSetSchema}

                            style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                        />
                    }
                    <ItemTypeDropdown
                        uid={uid}

                        schema={createNewOverviewDialogSchema}
                        value={createNewOverviewDialogItemType}
                        rawMode={false}
                        showAdditionalRawNames={false}
                        markDirty={false}
                        onChange={this.onCreateNewOverviewDialogSetItemType}
                        inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                    />
                </FieldWrapper>
            }
            {( createNewOverviewDialogType === 'Cypher' || createNewOverviewDialogType === 'SQL' ) &&
                <TextField
                    uid={`${uid}_queryStringField`}
                    label={__( 'YN.base.frontend.overviewDesigner.createNewDialog.queryString.label' )}
                    value={createNewOverviewDialogString}
                    errorMessage={createNewOverviewDialogStringError}
                    height={6}
                    labelPosition="top"
                    editMode="plaintext"
                    infoTextLayout="text"
                    markDirty={false}
                    onChange={this.onCreateNewOverviewDialogSetString}
                />
            }
        </Dialog>;
    }

    getHeaderbar( uid : string, dataContext : string, overviewId : number, overviewKey : string, overviewName : string ) {
        return <Headerbar
            key={`${uid}_headerbar`}

            dataContext={dataContext}

            overviewId={overviewId}
            overviewKey={overviewKey}
            overviewName={overviewName}
        />;
    }

    getToolbar( uid : string, dataContext : string, rootItemId : number, treeViewItem : Item, readOnly : boolean, additionalParameters : Required<LoadDataParameters>[ 'parameters' ] | undefined, mode : OverviewDesignerMode, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[], standalone : boolean, showData : boolean, showHiddenColumns : boolean, columnDefinitions : ColumnDefinition[] | undefined, columns : ColumnConfig[] | undefined, sorters : ColumnSorter[] | undefined, hasHiddenColumns : boolean | undefined ) {
        return <Toolbar
            key={`${uid}_toolbar`}

            uid={uid}

            dataContext={dataContext}
            schema="CFG"
            rootItemId={rootItemId}

            treeViewItem={treeViewItem}
            additionalParameters={additionalParameters}

            mode={mode}

            saveButtonDisabled={readOnly}
            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}
            deleteButtonDisabled={readOnly}

            standalone={standalone}
            showData={showData}
            showHiddenColumns={showHiddenColumns}

            columnDefinitions={columnDefinitions}
            columns={columns}
            sorters={sorters}

            onReloadButtonClick={this.onReloadButtonClick}
            onShowDataChange={this.onShowDataChange}
            onSortersChange={this.onSortersChange}
            onShowHiddenColumnsChange={this.onShowHiddenColumnsChange}

            showStandaloneSelector={mode === 'input'}
            showHiddenColumnsCheckbox={mode === 'design' && hasHiddenColumns}
        />;
    }

    getToolbarEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getTreeTable( uid : string, dataContext : string, treeProps : any, readOnly : boolean, additionalElusiveDataContexts : string[], selectedLayoutElementId : number | undefined, apiParams : TreeTableDefaultProps['apiParams'] ) {
        return <TreeTable
            key={uid + '_DataObjects'}

            ref={( el ) => this.tree = el}

            {...treeProps}

            className="yn-sidebar-content p-tree"
            dataContext={dataContext}
            uid={uid + '_DataObjects'}
            readOnly={readOnly}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            parentUid={uid}
            useElusiveDataFromDataContext={false}
            schema="CFG"
            selectedLayoutElementId={selectedLayoutElementId}
            onSelectLayoutElement={this.onSelectLayoutElement}
            onNewLayoutElementDragStart={this.onNewLayoutElementDragStart}
            onMarkLayoutElementsForAlias={this.onMarkLayoutElementsForAlias}
            keepExistingOnReload={true}
            apiParams={apiParams}
        />;
    }

    getTreeTableEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getOverviewDesigner( configUid : string, configDataContext : string, overviewProps : any, dataContext : string, configRootItemId : number, rootItemId : number | undefined, readOnly : boolean, uid : string, mode : OverviewDesignerMode, standalone : boolean, showData : boolean, showHiddenColumns : boolean, additionalElusiveDataContexts : string[], rootItemFormState : State['rootItemFormState'], additionalParameters ? : Required<LoadDataParameters>[ 'parameters' ], selectedLayoutElementId? : number ) {
        return <OverviewDesigner
            key={uid}

            ref={( el ) => this.overviewDesigner = el}

            {...overviewProps}

            uid={uid}
            configUid={configUid}

            readOnly={readOnly}

            mode={mode}
            standalone={standalone}
            showData={showData}
            showHiddenColumns={showHiddenColumns}

            configDataContext={configDataContext}
            configRootItemId={configRootItemId}

            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            rootItemId={rootItemId}
            rootItemFormState={rootItemFormState}
            additionalParameters={additionalParameters}

            selectedLayoutElementId={selectedLayoutElementId}
            onMarkFieldsForAlias={this.onMarkFieldsForAlias}
            onSelectLayoutElement={this.onSelectLayoutElement}
        />;
    }

    getOverviewDesignerEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, readOnly : boolean, treeParentItemId : number, treeRootItemId : number, treeVariables : Variables | undefined ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            readOnly={readOnly}
            rootItemId={rootItemId}
            rootItemKey=""
            rootItemType=""
            treeParentItemId={treeParentItemId}
            treeRootItemId={treeRootItemId}
            treeVariables={treeVariables}
        />
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  additionalParameters,
                  dataContext,
                  dataRootItemId,
                  elusiveData,
                  menu2Width,
                  menuWidth,
                  mode,
                  rootItemKey,
                  selectedLayoutElementId,
                  showData,
                  showHiddenColumns,
                  standalone,
                  status,
                  subPanelProps,
                  timestamp,
                  timezone,
                  uid,
                  user
              }                           = this.props,
              {
                  errorCount,
                  errorMessage,
                  errorTooltip,
                  initialized,
                  rootItemFormState,
                  rootItemId = 0,
                  showCreateNewOverviewDialog
              }                           = this.state,
              rootItem                    = global.data[ dataContext ]?.items?.[ rootItemId ],
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              readOnly                    = !( user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) || !rootItem?.properties.get( metaModel[ 'CFG' ].getPropertyType( 'SystemRelevant' )?.id ?? 0 )?.value ),
              rootDataContext             = `${dataContext}_data`,
              dataStructure               = this.overviewDesigner?.dataStructure,
              dataObjectItems             = dataStructure
                  ? extractDataObjectsFromDataStructure( dataStructure ).map( dO => global.data[ dataContext ]?.items?.[ dO.origId ] )
                  : [],
              rawMode                     = false,
              showAdditionalRawNames      = false,
              selectedLayoutElement       = selectedLayoutElementId ? global.data[ dataContext ]?.items[ selectedLayoutElementId ] : undefined,
              currentItemId               = selectedLayoutElement?.id ?? rootItemId,
              currentItem                 = selectedLayoutElement ?? rootItem,
              panelConfigs                = user && currentItem ? user.getPanelPropsForItemType( 'CFG', currentItem.itemTypeId, undefined, 'Form' ) as FormPanelProps[] : undefined,
              panelConfig                 = panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^OdSp_/ ) ) ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) ) ?? panelConfigs?.[ 0 ] ?? null,
              allBelongsToReferenceSetIds = currentItem ? metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( currentItem.itemTypeId, '*', 'belongsTo', 'src' )?.map( rset => rset.id ) : undefined,
              parentPayloadId             = dataUtils.findReferences( dataContext, allBelongsToReferenceSetIds, undefined, currentItemId, true )?.[ 0 ]?.destItemId ?? rootItemId,
              formUid                     = `form-${currentItemId}`,
              fullFormUid                 = `${uid}_${formUid}`,
              overviewDesignerUid         = `overviewDesigner-${rootItemId}`,
              fullOverviewDesignerUid     = `${uid}_${overviewDesignerUid}`,
              attributesUid               = `attributes-${currentItemId}`,
              fullAttributesUid           = `${uid}_${attributesUid}`;

        return <>
            {showCreateNewOverviewDialog &&
                this.getCreateNewOverviewDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {this.getHeaderbar(
                uid,
                dataContext,
                rootItemId,
                rootItem?.key ?? '',
                rootItem?.name ?? ''
            )}
            {this.getToolbar(
                uid,
                dataContext,
                rootItemId,
                rootItem,
                readOnly,
                additionalParameters,
                mode,
                errorCount,
                errorTooltip,
                standalone,
                showData,
                showHiddenColumns,
                this.overviewDesigner?.columnDefinitions,
                this.overviewDesigner?.columns,
                this.overviewDesigner?.sorters,
                this.overviewDesigner?.hasHiddenColumns
            )}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    menuClassName={mode === 'input' ? 'hidden' : undefined}
                    gutterSize={mode === 'input' ? 0 : undefined}
                    contentStyle={this.menuContentPanelContentStyle}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}

                    pt={mode === 'input'
                        ? this.menuContentPanelsPt
                        : undefined}

                    menu={initialized && !showCreateNewOverviewDialog
                        ? this.getTreeTable(
                            uid,
                            dataContext,
                            this.props.subPanelProps?.[ 'DataObjects' ],
                            readOnly,
                            [ uid + '_DataObjects', fullFormUid, uid ],
                            selectedLayoutElementId,
                            {
                                viewKey     : 'XdTvCit_Query',
                                schema      : 'CFG',
                                rootItemId,
                                rootItemKey,
                                rootItemType: 'Overview',
                                // TODO: set this flag only if metaData is requested for xml export
                                includeMetaData: true
                            }
                        )
                        : <></>
                    }
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            menuClassName={`yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel ${mode === 'input' ? 'hidden' : ''}`}
                            gutterSize={mode === 'input' ? 0 : undefined}
                            contentClassName={( mode === 'input' && standalone ) ? '' : undefined}
                            onMenuWidthChange={this.onMenu2WidthChange}

                            pt={mode === 'input'
                                ? this.menuContentPanelsPt
                                : undefined}

                            content={<LoadingMaskPanel forComponent={fullOverviewDesignerUid}
                                                       className={`yn-flex-layout-parent p-0 ${( mode === 'input' && standalone ) ? '' : 'yn-content-panel-container yn-formview-panel'}`}
                                                       style={{ position: 'absolute', width: '100% !important', height: '100% !important' }}>
                                {initialized && !showCreateNewOverviewDialog
                                    ? this.getOverviewDesigner(
                                        uid,
                                        dataContext,
                                        subPanelProps?.[ overviewDesignerUid ],
                                        rootDataContext,
                                        rootItemId,
                                        dataRootItemId,
                                        readOnly,
                                        fullOverviewDesignerUid,
                                        mode,
                                        standalone,
                                        showData,
                                        showHiddenColumns,
                                        [ fullFormUid, uid ],
                                        rootItemFormState,
                                        additionalParameters,
                                        selectedLayoutElementId ?? currentItemId
                                    )
                                    : undefined
                                }
                            </LoadingMaskPanel>}
                            menu={initialized && !showCreateNewOverviewDialog
                                ? <LoadingMaskPanel key={`${uid}_formview_loadingmask`}
                                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                                    className="yn-flex-layout-parent">
                                    <div>
                                        {panelConfig?.panelType === 'Form' && currentItemId
                                            ? this.getForm(
                                                fullFormUid,
                                                dataContext,
                                                subPanelProps?.[ formUid ],
                                                [ uid + '_DataObjects', uid ],
                                                panelConfig.viewKey,
                                                currentItemId,
                                                readOnly,
                                                parentPayloadId,
                                                rootItemId,
                                                {
                                                    dataStructure,
                                                    dataObjectItems
                                                }
                                            )
                                            : undefined
                                        }
                                        {panelConfig?.panelType !== 'Form' && currentItem &&
                                            <div key={`${uid}_formview`}>
                                                <AttributesForm
                                                    key={fullAttributesUid}

                                                    {...subPanelProps?.[ attributesUid ]}

                                                    uid={fullAttributesUid}
                                                    dataContext={dataContext}
                                                    style={{ padding: 0 }}
                                                    schema="CFG"
                                                    status={status}
                                                    timestamp={timestamp}
                                                    timezone={timezone}
                                                    item={currentItem}
                                                    rawMode={rawMode}
                                                    showAdditionalRawNames={showAdditionalRawNames}
                                                    editable={true}
                                                />
                                            </div>
                                        }
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }
                        />
                    </div>}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const retVal : Partial<Props> = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }

        // query params
        const queryParams = getQueryParams( ownProps.rootItemId, ownProps.dataContext, retVal.elusiveData?.data );
        if ( queryParams ) {
            retVal.additionalParameters = {};

            for ( const parameterKey of queryParams ) {
                retVal.additionalParameters[ parameterKey ] = ownProps.additionalParameters?.[ parameterKey ] ?? '';
            }
        }
        else {
            retVal.additionalParameters = undefined;
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( OverviewDesignerView );
